import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { getUploadUrl } from '../utils/api';
import { getUser, logout } from '../utils/auth';
import { FileText, LogOut, Clock, User, CheckCircle, XCircle, Layers, GraduationCap, BookOpen, Calendar, Building2, Archive, ChevronDown, ChevronUp } from 'lucide-react';

const StudentDashboard = () => {
  const navigate = useNavigate();
  const user = getUser();
  const [exams, setExams] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [school, setSchool] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showArchived, setShowArchived] = useState(false);

  const fetchData = async () => {
    try {
      const [examsRes, submissionsRes, schoolRes] = await Promise.all([
        api.get('/exams'),
        api.get('/submissions/student/my-submissions'),
        api.get('/school/info'),
      ]);
      setExams(examsRes.data);
      setSubmissions(submissionsRes.data);
      setSchool(schoolRes.data);
    } catch (err) {
      console.error('Failed to fetch data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!user || user.role !== 'student') {
      navigate('/');
      return;
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, navigate]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const hasSubmitted = (examId) => {
    return submissions.some(sub => sub.exam_id === examId);
  };

  const getSubmission = (examId) => {
    return submissions.find(sub => sub.exam_id === examId);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header with School Branding */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            {school?.logo_url ? (
              <img src={getUploadUrl(school.logo_url)} alt={school.name} className="w-12 h-12 rounded-lg object-cover" />
            ) : school ? (
              <div className="w-12 h-12 rounded-lg bg-indigo-100 flex items-center justify-center">
                <Building2 className="w-6 h-6 text-indigo-700" />
              </div>
            ) : null}
            <div>
              <h1 className="text-xl font-bold text-slate-900">{school?.name || 'Student Dashboard'}</h1>
              <p className="text-sm text-slate-600">Welcome, {user?.name}</p>
            </div>
          </div>
          <button
            data-testid="logout-btn"
            onClick={handleLogout}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-100 text-slate-600 hover:bg-slate-200 transition-all"
          >
            <LogOut className="w-4 h-4" />
            Logout
          </button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto p-6 md:p-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white p-6 rounded-xl border border-slate-100">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-indigo-100 rounded-lg">
                <FileText className="w-6 h-6 text-indigo-700" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Active Exams</p>
                <p className="text-2xl font-bold text-slate-900">
                  {exams.filter(e => e.status === 'active').length}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white p-6 rounded-xl border border-slate-100">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-emerald-100 rounded-lg">
                <CheckCircle className="w-6 h-6 text-emerald-700" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Completed</p>
                <p className="text-2xl font-bold text-slate-900">{submissions.length}</p>
              </div>
            </div>
          </div>
          <div className="bg-white p-6 rounded-xl border border-slate-100">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-slate-100 rounded-lg">
                <Clock className="w-6 h-6 text-slate-700" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Graded</p>
                <p className="text-2xl font-bold text-slate-900">
                  {submissions.filter(s => s.is_graded).length}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white p-6 rounded-xl border border-slate-100">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-amber-100 rounded-lg">
                <Archive className="w-6 h-6 text-amber-700" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Archived</p>
                <p className="text-2xl font-bold text-slate-900">
                  {exams.filter(e => e.status === 'closed').length}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Available Exams */}
        <div className="mb-12">
          <h2 className="text-2xl font-semibold text-slate-900 mb-6">Available Exams</h2>
          
          {loading ? (
            <div className="text-center py-12 text-slate-500">Loading exams...</div>
          ) : exams.filter(e => e.status === 'active').length === 0 ? (
            <div className="text-center py-12 bg-white rounded-xl border border-slate-200">
              <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-600">No active exams available</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {exams.filter(e => e.status === 'active').map((exam) => {
                const submitted = hasSubmitted(exam.id);
                return (
                  <div
                    key={exam.id}
                    data-testid={`exam-card-${exam.id}`}
                    className="bg-white border border-slate-200 rounded-xl p-6"
                  >
                    <h3 className="text-lg font-semibold text-slate-900 mb-2">
                      {exam.title}
                    </h3>
                    <p className="text-sm text-slate-600 mb-3">By {exam.teacher_name}</p>
                    
                    {/* Board/Grade/Subject tags */}
                    {(exam.board_name || exam.grade_name || exam.subject_name) && (
                      <div className="flex flex-wrap gap-1.5 mb-3">
                        {exam.board_name && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-indigo-50 text-indigo-700 rounded text-xs">
                            <Layers className="w-3 h-3" />
                            {exam.board_name}
                          </span>
                        )}
                        {exam.grade_name && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-50 text-green-700 rounded text-xs">
                            <GraduationCap className="w-3 h-3" />
                            {exam.grade_name}
                          </span>
                        )}
                        {exam.subject_name && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-50 text-amber-700 rounded text-xs">
                            <BookOpen className="w-3 h-3" />
                            {exam.subject_name}
                          </span>
                        )}
                      </div>
                    )}
                    
                    <div className="space-y-2 text-sm text-slate-600 mb-4">
                      <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4" />
                        <span>{exam.questions.length} questions</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4" />
                        <span>{exam.duration_minutes} minutes</span>
                      </div>
                      {/* Schedule Info */}
                      {(exam.start_datetime || exam.end_datetime) && (
                        <div className="pt-2 mt-2 border-t border-slate-100">
                          {exam.start_datetime && (
                            <div className="flex items-center gap-2 text-xs text-indigo-600">
                              <Calendar className="w-3.5 h-3.5" />
                              <span>Starts: {new Date(exam.start_datetime).toLocaleString()}</span>
                            </div>
                          )}
                          {exam.end_datetime && (
                            <div className="flex items-center gap-2 text-xs text-red-600 mt-1">
                              <Calendar className="w-3.5 h-3.5" />
                              <span>Ends: {new Date(exam.end_datetime).toLocaleString()}</span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {submitted ? (
                      <div className="px-4 py-2 bg-emerald-50 text-emerald-700 text-sm font-medium rounded-lg text-center">
                        Submitted
                      </div>
                    ) : (
                      <button
                        data-testid={`take-exam-${exam.id}`}
                        onClick={() => navigate(`/student/exam/${exam.id}`)}
                        className="w-full px-4 py-2 bg-indigo-700 hover:bg-indigo-800 text-white font-medium rounded-lg transition-all"
                      >
                        Take Exam
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Archived Exams Section for Students */}
          {exams.filter(e => e.status === 'closed').length > 0 && (
            <div className="mt-8">
              <button
                onClick={() => setShowArchived(!showArchived)}
                className="flex items-center gap-2 px-4 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium rounded-lg transition-all mb-4"
                data-testid="toggle-archived-btn"
              >
                <Archive className="w-5 h-5" />
                {showArchived ? 'Hide' : 'View'} Archived Exams ({exams.filter(e => e.status === 'closed').length})
                {showArchived ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>

              {showArchived && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {exams.filter(e => e.status === 'closed').map((exam) => {
                    const submitted = hasSubmitted(exam.id);
                    const submission = getSubmission(exam.id);
                    return (
                      <div
                        key={exam.id}
                        data-testid={`archived-exam-card-${exam.id}`}
                        className="bg-slate-50 border border-slate-200 rounded-xl p-6 opacity-80"
                      >
                        <div className="flex items-start justify-between mb-2">
                          <h3 className="text-lg font-semibold text-slate-700">
                            {exam.title}
                          </h3>
                          <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset bg-slate-100 text-slate-600 ring-slate-500/10">
                            ARCHIVED
                          </span>
                        </div>
                        <p className="text-sm text-slate-500 mb-3">By {exam.teacher_name}</p>
                        
                        {/* Board/Grade/Subject tags */}
                        {(exam.board_name || exam.grade_name || exam.subject_name) && (
                          <div className="flex flex-wrap gap-1.5 mb-3">
                            {exam.board_name && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-indigo-50 text-indigo-700 rounded text-xs">
                                <Layers className="w-3 h-3" />
                                {exam.board_name}
                              </span>
                            )}
                            {exam.grade_name && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-50 text-green-700 rounded text-xs">
                                <GraduationCap className="w-3 h-3" />
                                {exam.grade_name}
                              </span>
                            )}
                            {exam.subject_name && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-50 text-amber-700 rounded text-xs">
                                <BookOpen className="w-3 h-3" />
                                {exam.subject_name}
                              </span>
                            )}
                          </div>
                        )}
                        
                        <div className="space-y-2 text-sm text-slate-500 mb-4">
                          <div className="flex items-center gap-2">
                            <FileText className="w-4 h-4" />
                            <span>{exam.questions.length} questions</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Clock className="w-4 h-4" />
                            <span>{exam.duration_minutes} minutes</span>
                          </div>
                        </div>

                        {submitted ? (
                          submission?.is_graded ? (
                            <button
                              onClick={() => navigate(`/student/graded/${submission.id}`)}
                              className="w-full px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-lg transition-all"
                            >
                              View Graded Paper
                            </button>
                          ) : (
                            <div className="px-4 py-2 bg-emerald-50 text-emerald-700 text-sm font-medium rounded-lg text-center">
                              Submitted - Pending Grade
                            </div>
                          )
                        ) : (
                          <div className="px-4 py-2 bg-slate-100 text-slate-500 text-sm font-medium rounded-lg text-center">
                            Exam Closed
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        {/* My Submissions */}
        <div>
          <h2 className="text-2xl font-semibold text-slate-900 mb-6">My Submissions</h2>
          
          {submissions.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-xl border border-slate-200">
              <CheckCircle className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-600">No submissions yet</p>
            </div>
          ) : (
            <div className="space-y-4">
              {submissions.map((submission) => (
                <div
                  key={submission.id}
                  data-testid={`submission-card-${submission.id}`}
                  className="bg-white border border-slate-200 rounded-xl p-6 flex items-center justify-between"
                >
                  <div>
                    <h3 className="text-lg font-semibold text-slate-900 mb-1">
                      {submission.exam_title}
                    </h3>
                    <p className="text-sm text-slate-600">
                      Submitted on {new Date(submission.submitted_at).toLocaleDateString()}
                    </p>
                  </div>
                  
                  <div className="flex items-center gap-4">
                    {submission.is_graded ? (
                      <>
                        <div className="text-right">
                          <div className="text-2xl font-bold text-indigo-700 mb-1">
                            {submission.marks_allocated} marks
                          </div>
                          <div className="text-sm font-medium text-emerald-600">
                            Grade: {submission.grade}
                          </div>
                        </div>
                        <button
                          onClick={() => navigate(`/student/graded/${submission.id}`)}
                          className="px-6 py-2.5 bg-indigo-700 hover:bg-indigo-800 text-white font-medium rounded-lg transition-all"
                        >
                          View Graded Paper
                        </button>
                      </>
                    ) : (
                      <div className="text-sm text-slate-500">
                        Pending grading
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StudentDashboard;